import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  usePharmacySales,
  usePharmacySale,
  useTrashPharmacySale,
  useBulkTrashPharmacySales,
} from '../../services/httpServices/queries';
import type { PharmacySaleStatus } from '../../services/httpServices/pharmacySaleService';
import { useDebounce } from '../../hooks/useDebounce';
import { Pagination } from '../../components/ui/Pagination';
import { joinFullName } from '../../utils/patientName';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { DatePicker } from '../../components/ui/DatePicker';
import { printPharmacySaleReceipt } from '../../utils/thermalReceipt';
import {
  Loader2,
  AlertCircle,
  Search,
  X,
  User,
  Receipt,
  Pencil,
  Trash2,
  Eye,
  RotateCcw,
  Printer,
} from 'lucide-react';
import { PharmacySaleReturnDialog } from './PharmacySaleReturnDialog';

const LIMIT = 20;

const money = (value: number) => `৳${(Number(value) || 0).toLocaleString()}`;

const STATUS_COLORS: Record<string, string> = {
  COMPLETED: 'bg-success/10 text-success',
  PARTIAL: 'bg-warning/10 text-warning',
  PARTIALLY_REFUNDED: 'bg-warning/10 text-warning',
  REFUNDED: 'bg-primary/10 text-primary',
  VOID: 'bg-danger/10 text-danger',
};

const STATUS_OPTIONS: { value: 'all' | PharmacySaleStatus; label: string }[] = [
  { value: 'all', label: 'All Status' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIAL', label: 'Partial' },
  { value: 'PARTIALLY_REFUNDED', label: 'Partially Refunded' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'VOID', label: 'Void' },
];

/**
 * Counter sales list. Rendered as the "Sales" tab of the combined
 * pharmacy page (`/pharmacy`), so it intentionally has no page chrome.
 */
export function PharmacySalesPanel() {
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | PharmacySaleStatus>('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [returnSaleId, setReturnSaleId] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<{ mode: 'single' | 'bulk'; sale?: any } | null>(null);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  const filters = useMemo(
    () => ({
      search: debouncedSearch.trim() || undefined,
      status: statusFilter === 'all' ? undefined : statusFilter,
      from: fromDate ? `${fromDate}T00:00:00` : undefined,
      to: toDate ? `${toDate}T23:59:59` : undefined,
    }),
    [debouncedSearch, statusFilter, fromDate, toDate],
  );

  const { data, isLoading, error } = usePharmacySales(page, LIMIT, filters);
  const { data: detailData, isLoading: detailLoading } = usePharmacySale(selectedId || undefined);
  const { data: returnSaleData } = usePharmacySale(returnSaleId || undefined);
  const trashSale = useTrashPharmacySale();
  const bulkTrash = useBulkTrashPharmacySales();

  const sales = data?.data || [];
  const meta = data?.meta;
  const detail = detailData?.data;

  // Reset to page 1 and drop stale selections whenever filters change.
  useEffect(() => {
    setPage(1);
    setSelectedIds([]);
  }, [debouncedSearch, statusFilter, fromDate, toDate]);

  const allSelected = sales.length > 0 && sales.every((sale: any) => selectedIds.includes(sale.id));
  const someSelected = selectedIds.length > 0;

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = someSelected && !allSelected;
    }
  }, [someSelected, allSelected]);

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !sales.some((sale: any) => sale.id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...sales.map((sale: any) => sale.id)])));
    }
  };

  const toggleSelectOne = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const customerLabel = (sale: any) => {
    if (sale.patient) {
      const name = joinFullName(sale.patient);
      return name || sale.patient.registrationNumber || 'Patient';
    }
    return sale.customerName || 'Walk-in';
  };

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('all');
    setFromDate('');
    setToDate('');
  };

  /** A sale can be returned while it is not fully refunded and has un-returned units. */
  const canReturnSale = (sale: any) => {
    if (!sale || sale.status === 'REFUNDED') return false;
    const items: any[] = sale.items || [];
    return items.some(
      (item) => Number(item.quantity) - Number(item.returnedQuantity ?? 0) > 0,
    );
  };

  const hasFilters = Boolean(search || statusFilter !== 'all' || fromDate || toDate);

  const handleConfirmDelete = async () => {
    if (!confirmTarget) return;
    try {
      if (confirmTarget.mode === 'single') {
        await trashSale.mutateAsync(confirmTarget.sale.id);
      } else {
        await bulkTrash.mutateAsync(selectedIds);
        toast.success(`Moved ${selectedIds.length} sale(s) to trash`);
        setSelectedIds([]);
      }
    } catch {
      if (confirmTarget.mode === 'bulk') toast.error('Failed to move some sales to trash');
    } finally {
      setConfirmTarget(null);
    }
  };

  const bulkBusy = bulkTrash.isPending || trashSale.isPending;

  return (
    <div className="space-y-6">
      {/* Search + filters */}
      <div className="bg-surface rounded-xl border border-border p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by sale number, customer or patient..."
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-background text-text focus:outline-none focus:ring-2 focus:ring-primary text-sm"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-text focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className="flex items-center gap-2 px-3 py-1.5 bg-background border border-border rounded-lg">
            <DatePicker
              value={fromDate}
              onChange={setFromDate}
              className="w-auto border-0 bg-transparent px-0 py-0"
              title="From date"
            />
            <span className="text-text-muted text-sm">–</span>
            <DatePicker
              value={toDate}
              min={fromDate || undefined}
              onChange={setToDate}
              className="w-auto border-0 bg-transparent px-0 py-0"
              title="To date"
            />
          </div>

          {hasFilters && (
            <button
              onClick={clearFilters}
              className="px-3 py-2 text-text-muted hover:text-danger border border-border hover:border-danger rounded-lg transition-colors text-sm flex items-center gap-1.5 whitespace-nowrap"
            >
              <X size={14} />
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk actions */}
      {someSelected && (
        <div className="flex items-center gap-2 bg-primary/5 border border-primary/20 rounded-xl p-3">
          <span className="text-sm text-text mr-2">
            <span className="font-medium">{selectedIds.length}</span> selected
          </span>
          <button
            onClick={() => setConfirmTarget({ mode: 'bulk' })}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-lg bg-danger text-white text-xs font-medium hover:bg-danger/90 disabled:opacity-50 flex items-center gap-1.5"
          >
            {bulkTrash.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Move to Trash
          </button>
          <button
            onClick={() => setSelectedIds([])}
            disabled={bulkBusy}
            className="px-3 py-1.5 rounded-lg text-text-muted hover:text-text border border-border text-xs font-medium disabled:opacity-50 flex items-center gap-1.5"
          >
            <X size={12} />
            Clear selection
          </button>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 p-4 bg-danger/10 border border-danger/20 rounded-lg text-danger">
          <AlertCircle size={18} />
          <span>Failed to load sales</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-background/50">
                  <th className="px-4 py-3 text-left w-10">
                    <input
                      ref={headerCheckboxRef}
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      disabled={sales.length === 0}
                      className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer disabled:cursor-default"
                      title="Select all"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Sale</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Total</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Due</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-text-muted uppercase">Date</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-text-muted uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {sales.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-text-muted">
                      {hasFilters ? (
                        <span>
                          No sales match your filters.{' '}
                          <button onClick={clearFilters} className="text-primary hover:underline">
                            Clear filters
                          </button>
                        </span>
                      ) : (
                        'No sales recorded yet'
                      )}
                    </td>
                  </tr>
                ) : (
                  sales.map((sale: any) => (
                    <tr
                      key={sale.id}
                      onClick={() => setSelectedId(sale.id)}
                      className="hover:bg-background/50 transition-colors cursor-pointer"
                    >
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(sale.id)}
                          onChange={() => toggleSelectOne(sale.id)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary cursor-pointer"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm font-mono text-text">{sale.saleNumber}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                            <User size={13} className="text-primary" />
                          </div>
                          <span className="text-sm text-text">{customerLabel(sale)}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">{sale.items?.length || 0}</td>
                      <td className="px-4 py-3 text-sm text-text text-right font-medium">
                        {money(sale.totalAmount)}
                      </td>
                      <td className="px-4 py-3 text-sm text-right">
                        {Number(sale.dueAmount) > 0 ? (
                          <span className="text-warning font-medium">{money(sale.dueAmount)}</span>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            STATUS_COLORS[sale.status] || 'bg-text-muted/10 text-text-muted'
                          }`}
                        >
                          {sale.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-muted">
                        {new Date(sale.soldAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setSelectedId(sale.id)}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                            title="View"
                          >
                            <Eye size={16} />
                          </button>
                          {canReturnSale(sale) && (
                            <button
                              onClick={() => setReturnSaleId(sale.id)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="Return / refund"
                            >
                              <RotateCcw size={16} />
                            </button>
                          )}
                          {!(Number(sale.refundedAmount) > 0) && (
                            <button
                              onClick={() => navigate(`/pharmacy/${sale.id}/edit`)}
                              className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                              title="Edit"
                            >
                              <Pencil size={16} />
                            </button>
                          )}
                          <button
                            onClick={() => setConfirmTarget({ mode: 'single', sale })}
                            disabled={trashSale.isPending}
                            className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-danger transition-colors disabled:opacity-50"
                            title="Move to trash"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {meta && (
            <Pagination
              page={meta.page}
              totalPages={meta.totalPages}
              total={meta.total}
              limit={meta.limit}
              onPageChange={setPage}
              itemName="sales"
            />
          )}
        </div>
      )}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={confirmTarget !== null}
        title={confirmTarget?.mode === 'bulk' ? 'Move sales to trash' : 'Move sale to trash'}
        description={
          confirmTarget?.mode === 'bulk' ? (
            `Move ${selectedIds.length} selected sale(s) to the trash? You can restore them later.`
          ) : (
            <>
              Move <strong>{confirmTarget?.sale?.saleNumber}</strong> to the trash? You can restore it
              later.
            </>
          )
        }
        confirmLabel="Move to Trash"
        loading={bulkBusy}
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmTarget(null)}
      />

      {/* Detail modal */}
      {selectedId && !returnSaleId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-surface rounded-xl border border-border w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-primary" />
                <h2 className="text-lg font-semibold text-text">
                  {detail?.saleNumber || 'Sale details'}
                </h2>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => detail && void printPharmacySaleReceipt(detail)}
                  disabled={!detail}
                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors disabled:opacity-40"
                  title="Print receipt"
                >
                  <Printer size={16} />
                </button>
                {canReturnSale(detail) && (
                  <button
                    onClick={() => setReturnSaleId(detail?.id ?? null)}
                    className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                    title="Return / refund"
                  >
                    <RotateCcw size={16} />
                  </button>
                )}
                {detail && !(Number(detail.refundedAmount) > 0) && (
                  <button
                    onClick={() => {
                      setSelectedId(null);
                      navigate(`/pharmacy/${selectedId}/edit`);
                    }}
                    className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-primary transition-colors"
                    title="Edit sale"
                  >
                    <Pencil size={16} />
                  </button>
                )}
                <button
                  onClick={() => setSelectedId(null)}
                  className="p-1.5 rounded-lg hover:bg-background text-text-muted hover:text-text transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {detailLoading || !detail ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-text-muted uppercase">Customer</p>
                    <p className="text-text">{customerLabel(detail)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-text-muted uppercase">Date</p>
                    <p className="text-text">{new Date(detail.soldAt).toLocaleString()}</p>
                  </div>
                </div>

                <div className="border border-border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background/50 border-b border-border">
                        <th className="px-3 py-2 text-left text-xs text-text-muted uppercase">Item</th>
                        <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Qty</th>
                        <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Returned</th>
                        <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Price</th>
                        <th className="px-3 py-2 text-right text-xs text-text-muted uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {(detail.items || []).map((item: any) => (
                        <tr key={item.id}>
                          <td className="px-3 py-2">
                            <span className="text-text">{item.itemName}</span>
                            {item.itemType === 'ACCESSORY' && (
                              <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-background border border-border text-text-muted">
                                Accessory
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-text-muted">{item.quantity}</td>
                          <td className="px-3 py-2 text-right">
                            {Number(item.returnedQuantity) > 0 ? (
                              <span className="text-warning font-medium">
                                {Number(item.returnedQuantity)}/{Number(item.quantity)}
                              </span>
                            ) : (
                              <span className="text-text-muted">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-text-muted">{money(item.unitPrice)}</td>
                          <td className="px-3 py-2 text-right text-text font-medium">{money(item.totalPrice)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="rounded-lg border border-border bg-background/50 p-3 text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Subtotal</span>
                    <span className="text-text">{money(detail.subtotal)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Discount</span>
                    <span className="text-text">-{money(detail.discountAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between border-t border-border pt-1">
                    <span className="text-text font-semibold">Total</span>
                    <span className="text-text font-bold">{money(detail.totalAmount)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-text-muted">Paid</span>
                    <span className="text-text">{money(detail.paidAmount)}</span>
                  </div>
                  {Number(detail.dueAmount) > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">Due</span>
                      <span className="text-warning font-semibold">{money(detail.dueAmount)}</span>
                    </div>
                  )}
                  {Number(detail.refundedAmount) > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-text-muted">Refunded</span>
                      <span className="text-danger font-medium">-{money(Number(detail.refundedAmount))}</span>
                    </div>
                  )}
                </div>

                {(detail.payments || []).length > 0 && (
                  <div>
                    <p className="text-xs text-text-muted uppercase mb-1">Payments</p>
                    <div className="space-y-1">
                      {detail.payments!.map((payment: any) => (
                        <div key={payment.id} className="flex items-center justify-between text-sm">
                          <span className="text-text-muted capitalize">
                            {String(payment.method).replace('_', ' ')}
                            {payment.reference ? ` • ${payment.reference}` : ''}
                          </span>
                          <span className="text-text">{money(payment.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(detail.returns || []).length > 0 && (
                  <div>
                    <p className="text-xs text-text-muted uppercase mb-1">Returns</p>
                    <div className="space-y-2">
                      {detail.returns!.map((ret: any) => (
                        <div
                          key={ret.id}
                          className="rounded-lg border border-border bg-background/50 p-2 text-sm space-y-1"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-mono text-xs text-text">{ret.returnNumber}</span>
                            <span className="text-text font-medium">{money(ret.refundAmount)}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs text-text-muted">
                            <span>{new Date(ret.returnedAt).toLocaleString()}</span>
                            {ret.refundMethod && (
                              <span className="capitalize">
                                {String(ret.refundMethod).replace('_', ' ')}
                              </span>
                            )}
                          </div>
                          {(ret.items || []).length > 0 && (
                            <p className="text-xs text-text-muted">
                              {ret.items
                                .map((line: any) => `${line.itemName} × ${Number(line.quantity)}`)
                                .join(', ')}
                            </p>
                          )}
                          {ret.reason && <p className="text-xs text-text-muted">Reason: {ret.reason}</p>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {detail.notes && (
                  <div>
                    <p className="text-xs text-text-muted uppercase mb-1">Note</p>
                    <p className="text-sm text-text">{detail.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {returnSaleId && returnSaleData?.data && (
        <PharmacySaleReturnDialog
          sale={returnSaleData.data}
          onClose={() => setReturnSaleId(null)}
        />
      )}

      {returnSaleId && !returnSaleData?.data && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      )}
    </div>
  );
}
